import { useState } from "react";
import { useLenis } from "lenis/react";
import { Wrench, ArrowUp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../ui/dialog";
import { NAV_LINKS, CONTACT } from "../../data/site";

const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    body: "Autofficina Ciro Natale tratta i dati personali dei clienti nel rispetto del Regolamento UE 2016/679 (GDPR). I dati raccolti tramite contatto telefonico, email o WhatsApp vengono utilizzati esclusivamente per rispondere alle richieste e gestire gli appuntamenti. I dati non vengono ceduti a terzi. In qualsiasi momento è possibile richiederne la modifica o la cancellazione contattando l'officina.",
  },
  cookie: {
    title: "Cookie Policy",
    body: "Questo sito utilizza esclusivamente cookie tecnici necessari al corretto funzionamento delle pagine e all'incorporamento della mappa di Google. Non vengono utilizzati cookie di profilazione a fini di marketing. Continuando la navigazione acconsenti all'uso dei cookie tecnici.",
  },
};

const LegalDialog = ({ type, testid, label }) => {
  const { title, body } = LEGAL[type];
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid={testid}
          className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] transition-colors duration-300 hover:text-[#FF2A2A]"
        >
          {label || title}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-none border border-white/10 bg-[#121212] text-[#FAFAFA]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">
            {title}
          </DialogTitle>
          <DialogDescription className="pt-4 font-body text-sm leading-relaxed text-[#A1A1AA]">
            {body}
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};

export const Footer = () => {
  const lenis = useLenis();
  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    lenis ? lenis.scrollTo(el, { offset: -70 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/10 bg-[#0A0A0A] px-5 py-16 md:px-10"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center border border-[#FF2A2A] text-[#FF2A2A]">
                <Wrench className="h-4 w-4" strokeWidth={2} />
              </span>
              <span className="font-display text-lg font-bold uppercase tracking-tight">
                Autofficina Ciro Natale
              </span>
            </div>
            <p className="mt-5 max-w-xs font-body text-sm leading-relaxed text-[#A1A1AA]">
              Esperienza, tecnologia e passione al servizio della tua auto dal
              1972.
            </p>
          </div>

          <div>
            <p className="mb-5 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#52525B]">
              Navigazione
            </p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.target}>
                  <button
                    data-testid={`footer-link-${l.target}`}
                    onClick={() => go(l.target)}
                    className="font-body text-sm text-[#A1A1AA] transition-colors duration-300 hover:text-[#FAFAFA]"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="mb-5 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#52525B]">
              Contatti
            </p>
            <ul className="space-y-3 font-body text-sm text-[#A1A1AA]">
              <li>{CONTACT.phone}</li>
              <li className="break-all">{CONTACT.email}</li>
              <li>{CONTACT.address}</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-center gap-6 border-t border-white/10 pt-8 md:flex-row">
          <div className="flex items-center gap-6">
            <LegalDialog type="privacy" testid="footer-privacy" label="Privacy Policy" />
            <LegalDialog type="cookie" testid="footer-cookie" label="Cookie" />
            <button
              data-testid="footer-scroll-top"
              onClick={() => go("hero")}
              className="flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#FAFAFA] transition-colors duration-300 hover:text-[#FF2A2A]"
            >
              Top <ArrowUp className="h-3 w-3" />
            </button>
          </div>
        </div>

        <div className="mt-8 flex justify-center border-t border-white/5 pt-8">
          <a
            href="https://wl-studio.it"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wl-studio-credit"
            aria-label="WL Studio — Freelance Web & Booking Apps"
            title="Realizzato da WL Studio"
            className="group flex items-center gap-3"
          >
            <span className="transition-transform duration-300 group-hover:scale-110 group-hover:brightness-125">
              <img
                src="/logo-wl.webp"
                alt="WL Studio"
                loading="lazy"
                width="1851"
                height="1332"
                className="h-9 w-auto"
              />
            </span>
            <span className="leading-tight">
              <span className="block font-display text-sm font-semibold uppercase tracking-wide text-[#FAFAFA] transition-colors duration-300 group-hover:text-[#FF2A2A]">
                WL Studio
              </span>
              <span className="block font-mono-accent text-[9px] uppercase tracking-[0.18em] text-[#52525B]">
                © 2026 — Freelance Web &amp; Booking Apps
              </span>
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
};
